import { defineStore } from "pinia";
import { ref, reactive } from "vue";
import svc from "@/services/finance/estimates.service";
import { toast } from "@/utils/toast";
import * as Toast from "@/utils/toast";
import { MODULE_KEYS } from "@/config/modules-registry";
import { EMPTY_PAGINATED_RESPONSE, guardPlanModule } from "@/utils/planAccess";

const notifyError = (msg) => {
  if (Toast?.toast?.error) return Toast.toast.error(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "error");
  if (Toast?.error) return Toast.error(msg);
  if (typeof Toast === "function") return Toast(msg, "error");
  console.error(msg);
};

const notifySuccess = (msg) => {
  if (Toast?.toast?.success) return Toast.toast.success(msg);
  if (typeof Toast?.toast === "function") return Toast.toast(msg, "success");
  if (Toast?.success) return Toast.success(msg);
  if (typeof Toast === "function") return Toast(msg, "success");
  console.log(msg);
};

export const useEstimateStore = defineStore("estimate", () => {
  const items = ref([]);
  const current = ref(null);
  const loading = ref(false);
  const loadingForRow = ref(false);
  const loadingForDelete = ref(false);
  const loadingForConvert = ref(false);
  const error = ref(null);

  const meta = ref({
    total: 0,
    per_page: 25,
    current_page: 1,
    last_page: 1,
  });

  const filters = reactive({
    q: "",
    status: "",
    from_date: "",
    to_date: "",
  });

  async function fetch(params = {}) {
    if (!(await guardPlanModule(MODULE_KEYS.FA_SALES))) {
      items.value = [];
      return EMPTY_PAGINATED_RESPONSE;
    }

    loading.value = true;
    error.value = null;
    try {
      const res = await svc.fetch({
        ...filters,
        ...params,
        page: params.page || meta.value.current_page,
      });

      items.value = res.data.data || [];
      if (res.data.meta) {
        meta.value = res.data.meta;
      }
    } catch (e) {
      error.value = e;
      console.error("Failed to load estimates", e);
    } finally {
      loading.value = false;
    }
  }

  async function fetchOne(id) {
    loadingForRow.value = true;
    try {
      const res = await svc.show(id);
      current.value = res.data.data;
      return current.value;
    } catch (e) {
      error.value = e;
      console.error("Failed to load estimate", e);
    } finally {
      loadingForRow.value = false;
    }
  }

  async function create(payload) {
    loading.value = true;
    try {
      const res = await svc.create(payload);
      notifySuccess("Estimate saved");
      await fetch({ page: 1 });
      return res.data.data;
    } catch (e) {
      error.value = e;
      notifyError("Failed to save estimate");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function update(id, payload) {
    loading.value = true;
    try {
      const res = await svc.update(id, payload);
      notifySuccess("Estimate updated");
      await fetch();
      return res.data.data;
    } catch (e) {
      error.value = e;
      notifyError("Failed to update estimate");
      throw e;
    } finally {
      loading.value = false;
    }
  }

  async function remove(id) {
    loadingForDelete.value = true;
    try {
      await svc.remove(id);
      notifySuccess("Estimate deleted");
      await fetch();
    } catch (e) {
      error.value = e;
      notifyError("Failed to delete estimate");
      throw e;
    } finally {
      loadingForDelete.value = false;
    }
  }

  async function convertToInvoice(id, extra = {}) {
    loadingForConvert.value = true;
    try {
      const res = await svc.convertToInvoice(id, extra);
      notifySuccess("Estimate converted to invoice");
      return res.data.data || res.data;
    } catch (e) {
      error.value = e;
      notifyError("Failed to convert estimate");
      throw e;
    }
    finally {
      loadingForConvert.value = false;
    }
  }

  async function exportPdf(params = {}) {
    return svc.exportPdf({
      ...filters,
      ...params,
    });
  }

  async function exportExcel(params = {}) {
    return svc.exportExcel({
      ...filters,
      ...params,
    });
  }

  return {
    items,
    current,
    loading,
    loadingForDelete,
    loadingForConvert,
    error,
    meta,
    filters,
    fetch,
    fetchOne,
    create,
    update,
    remove,
    convertToInvoice,
    exportPdf,
    exportExcel,
  };
});
